/**
 * ProfileSettings Component
 *
 * Form for editing user profile information including name and phone.
 * Provides validation, loading states, and success/error feedback.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-151 - Shared Account Settings
 *
 * Props:
 * - accountData: object - Current account data
 * - onUpdate: function - Callback when profile is updated successfully
 */

import React, { useState, useMemo } from 'react';
import apiRequest from '../../services/api';
import { User, Phone, Mail, Save, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

export default function ProfileSettings({ accountData, onUpdate }) {
  // Form state
  const [formData, setFormData] = useState({
    first_name: accountData?.first_name || '',
    last_name: accountData?.last_name || '',
    phone: accountData?.phone || '',
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  /**
   * Handle input changes
   */
  const handleChange = (_e) => {
    const { name, value } = _e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setHasChanges(true);
    setSuccess(false);
    setError(null);
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    if (!formData.first_name?.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.last_name?.trim()) {
      setError('Last name is required');
      return false;
    }
    return true;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (_e) => {
    _e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response = await apiRequest('/api/v1/account/profile', {
        method: 'PUT',
        body: JSON.stringify(formData)
      });

      if (response.success) {
        setSuccess(true);
        setHasChanges(false);
        onUpdate(response.data.data);

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calculate profile completeness (MVP 1.21.5 Story 4.3)
   */
  const profileCompletion = useMemo(() => {
    const requiredFields = {
      basic: ['first_name', 'last_name', 'email', 'phone', 'bio', 'profile_photo'],
      guide: ['certifications', 'insurance', 'experience_count']
    };

    const isGuide = accountData?.role === 'guide';
    const allRequiredFields = isGuide
      ? [...requiredFields.basic, ...requiredFields.guide]
      : requiredFields.basic;

    const completedFields = allRequiredFields.filter(field => {
      // eslint-disable-next-line security/detect-object-injection -- safe: field is from controlled requiredFields array
      const value = accountData?.[field];
      return value !== null && value !== undefined && value !== '';
    });

    const missingFields = allRequiredFields.filter(field => {
      // eslint-disable-next-line security/detect-object-injection -- safe: field is from controlled requiredFields array
      const value = accountData?.[field];
      return value === null || value === undefined || value === '';
    });

    const percentage = Math.round((completedFields.length / allRequiredFields.length) * 100);

    // Human-readable field names
    const fieldLabels = {
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
      phone: 'Phone Number',
      bio: 'Bio',
      profile_photo: 'Profile Photo',
      certifications: 'Certifications',
      insurance: 'Insurance',
      experience_count: 'Number of Experiences'
    };

    return {
      percentage,
      completedCount: completedFields.length,
      totalCount: allRequiredFields.length,
      // eslint-disable-next-line security/detect-object-injection -- safe: field is from controlled requiredFields array
      missingFields: missingFields.map(field => fieldLabels[field] || field),
      isComplete: percentage === 100
    };
  }, [accountData]);

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Profile Information</h3>
      <p className="text-sm text-gray-600 mb-6">
        Update your personal information. Your email address cannot be changed.
      </p>

      {/* MVP 1.21.5 Story 4.3: Profile Completion Indicator */}
      {!profileCompletion.isComplete && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-yellow-900">
                  Profile {profileCompletion.percentage}% Complete
                </h4>
                <span className="text-sm font-bold text-yellow-700">
                  {profileCompletion.percentage}%
                </span>
              </div>

              {/* Progress Bar */}
              <div className="w-full h-2 bg-yellow-100 rounded-full overflow-hidden mb-3">
                <div
                  className="h-full bg-yellow-500 transition-all duration-300 rounded-full"
                  style={{ width: `${profileCompletion.percentage}%` }}
                />
              </div>

              <p className="text-sm text-yellow-800 mb-2">
                Complete your profile to unlock all features and improve your experience.
              </p>

              {/* Missing Fields List */}
              {profileCompletion.missingFields.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-yellow-900 mb-1 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Missing fields:
                  </p>
                  <ul className="text-xs text-yellow-700 list-disc list-inside space-y-0.5">
                    {profileCompletion.missingFields.map((field, index) => (
                      <li key={index}>{field}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Profile Complete Banner */}
      {profileCompletion.isComplete && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-green-900">
                Profile 100% Complete
              </h4>
              <p className="text-sm text-green-700">
                Your profile is complete and looks great!
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email (Read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="w-4 h-4 inline mr-2" />
            Email Address
          </label>
          <input
            type="email"
            value={accountData?.email || ''}
            disabled
            className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed"
          />
          <p className="text-xs text-gray-500 mt-1">
            Email address cannot be changed
          </p>
        </div>

        {/* First Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-2" />
            First Name
            <span className="text-red-600 ml-1">*</span>
          </label>
          <input
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
            maxLength={50}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-shadow"
            placeholder="Enter your first name"
          />
        </div>

        {/* Last Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-2" />
            Last Name
            <span className="text-red-600 ml-1">*</span>
          </label>
          <input
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required
            maxLength={50}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-shadow"
            placeholder="Enter your last name"
          />
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="w-4 h-4 inline mr-2" />
            Phone Number
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            maxLength={20}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-shadow"
            placeholder="(555) 123-4567"
          />
          <p className="text-xs text-gray-500 mt-1">
            Optional - Used for booking notifications
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800 font-medium">
              Profile updated successfully!
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!hasChanges || loading}
            className={`
              flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors
              ${!hasChanges || loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-brand-primary text-white hover:bg-brand-primary-dark'
              }
            `}
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
